/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { getEventId, getSmartRoute } from "Router/RoutesUtils";
import { getEnvs } from "state/EnvironmentsContext/EnvContext";
import applySlashURL from "utils/applySlashURL";
import jsonParse from "utils/jsonParse";
import openToast from "utils/openToast";
import sleep from "utils/sleep";

// A manager for sending to the server.
class FetchBase {
  constructor() {
    const { urlMap } = getEnvs((state) => state);
    this.URLS = urlMap;
    this.isPaused = false;
    this.pendingRequests = {};
    return this;
  }

  #handleOutdated = async (response) => {
    if (response.status === 204 || response.ok === false) return response;
    const data = await response.clone().json();
    let appVersion;
    if (data.hasOwnProperty("reactapp_v")) {
      appVersion = data.reactapp_v;
    }

    if (appVersion) {
      const localVersion = localStorage.getItem("version");
      if (appVersion !== localVersion) {
        localStorage.setItem("version", appVersion);
        openToast("New version available, the page will be refreshed...");

        setTimeout(() => {
          window.location.reload(true);
        }, 3000);
      }
      // to not continue with the request and wait for the page to reload.
      await sleep(5000);
    }

    if (data.hasOwnProperty("organization_env_v")) {
      window.localStorage.setItem("env_version", data.organization_env_v);
    }

    return response;
  };

  #getBaseURL = (version) => {
    if (version === "1" || version === 1) {
      return this.URLS.v1;
    }

    if (version === "2" || version === 2) {
      return this.URLS.v2;
    }

    if (version === "3" || version === 3) {
      return this.URLS.v3;
    }
  };

  pause = () => {
    this.isPaused = true;
  };

  resume = () => {
    this.isPaused = false;
  };

  abortAll = () => {
    Object.keys(this.pendingRequests).forEach((instanceKey) => {
      const instance = this.pendingRequests[instanceKey];
      instance.abortController.abort("ABORTED");
    });
  };

  fetchBase = async (url, options, version = "1", fetchOpts) => {
    const abortController = new AbortController();
    this.pendingRequests[url] = { abortController, url };
    try {
      while (this.isPaused) {
        await sleep(100);
      }

      const settings = {
        shouldHandleError: true,
        shouldHandleOutdated: false,
        hasContentType: true,
      };

      if (fetchOpts) {
        Object.assign(settings, fetchOpts);
      }

      const baseURL = this.#getBaseURL(version);

      const opts = {
        headers: {},
        signal: abortController.signal,
      };

      if (options) {
        Object.assign(opts, options);
      }

      opts.headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        // to spread headers in case if options is true, else it will be empty so nothing will be added.
        ...opts.headers,
      };

      if (settings.hasContentType) {
        opts.headers["Content-type"] = "application/json; charset=UTF-8";
      }

      const fetchLink = applySlashURL(baseURL, url);
      const res = await fetch(fetchLink, opts);
      // No longer a pending request.
      delete this.pendingRequests[url];

      if (settings.shouldHandleOutdated) {
        await this.#handleOutdated(res);
      }

      if (settings.shouldHandleError && res.ok === false) {
        // will be caught by FetchError.
        throw res.clone();
      }

      return res;
    } catch (error) {
      if (abortController.signal.aborted) throw "ABORTED";
      throw error;
    }
  };
}

// A manager for before sending the request.
class FetchInterceptor {
  constructor() {
    return this.fetchInterceptors;
  }

  #parseRequestWithEventId = (request) => {
    let eventId = getEventId();
    if (eventId) {
      eventId = JSON.parse(+eventId);
    }

    let isFormData = false;
    let body = request.body;
    if (body instanceof FormData) {
      isFormData = true;
      body.append("event_id", eventId);
    } else {
      body = JSON.parse(request.body);
      body.event_id = eventId;
    }

    request.body = isFormData ? body : JSON.stringify(body);
    return request;
  };

  #parseURLwithEventId = (pathname) => {
    const isLogin = pathname.includes("login");
    if (isLogin) return pathname;

    const includeEventId = (pathname) => {
      const eventId = getEventId();
      if (pathname.includes("?")) {
        pathname = pathname + "&event_id=" + eventId;
      } else {
        pathname = pathname + "?event_id=" + eventId;
      }

      return pathname;
    };

    return includeEventId(pathname);
  };

  #encryption = (body) => {
    const _body = jsonParse(body);
    // TBD
    return JSON.stringify(_body);
  };

  #encryptRequest = (request) => {
    request.body = this.#encryption(request.body);
    return request;
  };

  fetchInterceptors = (version, url, request, options) => {
    const settings = {
      hasSubpath: true,
      shouldSearchForPage: true,
      eventTarget: null, // for event_id ("url" for get requests || "body" for post requests)
      page: null,
      encrypt: null,
    };
    // The reason I am doing this here, is that all GET requests are going to have the event_id in the url,
    // while some POST requests are going to have the event_id in the body, and some are going to have it in the url.
    if (
      !request ||
      request.method === "GET" ||
      !request.hasOwnProperty("method")
    ) {
      settings.eventTarget = "url";
    }

    if (options) {
      Object.assign(settings, options);
    }

    let pathname = url;
    if (settings.hasSubpath) {
      const page = settings.page;
      if (page) {
        const pageName = page;

        pathname = pageName + "/" + url;
      } else {
        pathname = getSmartRoute("API") + "/" + url;
      }
    }

    if (settings?.eventTarget) {
      if (settings?.eventTarget === "url") {
        // The get requests
        pathname = this.#parseURLwithEventId(pathname, request);
      }

      if (settings?.eventTarget === "body") {
        // "event_id" is going to be injected into the body, while "event_ids" is going to be selected by the user.
        request = this.#parseRequestWithEventId(request);
      }
    }

    if (settings?.encrypt && request.hasOwnProperty("body") && request.body) {
      request = this.#encryptRequest(request);
    }

    return [pathname, request, version, options];
  };
}

const fetchController = new FetchBase();
const fetchBase = fetchController.fetchBase;
const fetchInterceptor = new FetchInterceptor();

export { fetchBase, fetchController, fetchInterceptor };
